import { Checkbox, FormControl, FormControlLabel, MenuItem, Select } from '@mui/material';
import React, { useContext, useMemo } from "react";
import { ALLERGENS_PATH, DESSERT_DAYS_PATH, DISCOUNT_STAFF_PATH, menuPath, MODEL_COMPLEX_PATH, SCHOOL_DATA_PATH, SIDE_DAYS_PATH, studentsPath } from "../../functions/src/admin/paths";
import { PORTION_SIZE_MEDIUM, PORTION_SIZE_NORMAL, PORTION_SIZE_SMALL, SCHOOL_TYPE_NURSERY, SCHOOL_TYPE_PRIMARY, SCHOOL_TYPE_SECONDARY } from "../../functions/src/api/constants";
import { toDateFromServer } from "../../functions/src/api/dates";
import { canOrderMenuMealsForDay } from "../../functions/src/common/orderDeadlines";
import { filterMealsByDateAvailability } from '../../functions/src/common/mealAvailability';
import { getPackageExchangeMealPrice, getPersonnelPrice } from "../../functions/src/common/pricing";
import { Meal, MealLine, ModelComplexity, OrderPerson, SchoolData } from "../../functions/src/types";
import AllergenPdfDownloadLink from "./AllergenPdfDownloadLink";
import { auth, currentMenu, DataSnapshot } from "../api/firebase";
import { useDatabase, useDatabaseVal, useList, useSnapshotState, useStorageUrl } from "../api/firebaseHooks";
import { getLangValue, langContext } from "../api/Lang";
import { shouldShowPortionSize } from "../api/mealUtils";
import { usePortionSizeLabels } from "../api/portionUtils";
import { formatPriceEuro } from "../api/priceFormat";
import { useStrings } from "../common-strings";
import { Allergens } from "../types";
import { MealAllergensIndicator, MealPopupCategory, MealPopupFoodItemLayout, MEALS_POPUP_OVERLAY_Z_INDEX, getMealAllergenLabels } from "./mealPopupShared";
import { useOrderStrings } from "./order-strings";
import { useOrderDeadlineAt } from "./useOrderDeadlineAt";

export default function PackageMealsPopUp({ date, mealsList, dailyMealsList, typesList, sMealLine, discount }: PackageMealsPopUpProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();
    const mSnapshot = useDatabase(`${menuPath(currentMenu)}/${date}`);
    const loading = !mSnapshot;
    const list = useList(mSnapshot);

    const orderDate = useMemo(() => toDateFromServer(date), [date]);
    discount = discount ?? 0;
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const deadlineAt = useOrderDeadlineAt();

    const canOrderMealMenu = !!deadlineAt && canOrderMenuMealsForDay(date, modelConstants, deadlineAt);

    const sideDish: Meal = mSnapshot?.child('sideMenu')?.val();
    const dessert: Meal = mSnapshot?.child('dessertMenu')?.val();

    const setMenuItems = useMemo(
        () => list?.filter(sMeal => sMeal.key != 'sideMenu' && sMeal.key != 'dessertMenu') ?? [],
        [list],
    );
    const numOfSetMenus = setMenuItems.length;

    const lastEverydayMealKey = useMemo(() => {
        let lastKey: string | undefined;
        typesList.filter(s_type => s_type.child('order').val() > 0).forEach(sType => {
            if (sType.key === 'raw' || sType.key === 'main') return;
            const courseTypeFiltered = dailyMealsList?.filter(sMeal =>
                sMeal.child('courseType').val() === sType.key &&
                sMeal.child('order')?.val() >= 0 &&
                !sMeal.child('isSide')?.val() &&
                !sMeal.child('isDessert')?.val() &&
                sMeal.child('isExchangable')?.val() === true
            ) || [];
            const filteredMeals = filterMealsByDateAvailability(courseTypeFiltered, date);
            if (filteredMeals.length > 0)
                lastKey = filteredMeals[filteredMeals.length - 1].key;
        });
        return lastKey;
    }, [typesList, dailyMealsList, date]);

    const hasEverydayMeals = useMemo(() => {
        return typesList.some(sType => {
            if (sType.child('order').val() <= 0 || sType.key === 'raw' || sType.key === 'main') return false;
            const courseTypeFiltered = dailyMealsList?.filter(sMeal =>
                sMeal.child('courseType').val() === sType.key &&
                sMeal.child('order')?.val() >= 0 &&
                !sMeal.child('isSide')?.val() &&
                !sMeal.child('isDessert')?.val() &&
                sMeal.child('isExchangable')?.val() === true
            ) || [];
            return filterMealsByDateAvailability(courseTypeFiltered, date)?.length > 0;
        });
    }, [typesList, dailyMealsList, date]);

    return <div className="meals-popup package-meals-popup" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <div className={'change-package-title'}>{orderStrings.packageMealsPopupChangeTitle}</div>
        <div className={'menu-description'} style={{ textAlign: 'center' }}>{orderStrings.packageMealsPopupIntro}</div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '40px' }}>
            <div className={'popup-heading-meals'}>{orderStrings.mealsPopupSetMenusHeading}</div>
            <AllergenPdfDownloadLink
                lang={lang}
                targetDate={orderDate}
                label={orderStrings.mealsPopupAllergensList}
                style={{ marginTop: '32px' }}
            />
        </div>
        <div className={'menu-description'}>
            {numOfSetMenus === 1 ? (
                orderStrings.mealsPopupOneSetMenuDescription
            ) : (
                <>{orderStrings.mealsPopupSetMenusOfferPrefix}{numOfSetMenus}{orderStrings.mealsPopupSetMenusOfferSuffix}</>
            )}
        </div>

        {loading && commonStrings.loading
            || setMenuItems.map((listKey, index) =>
                <MealPopUpLine
                    key={listKey.key}
                    date={date}
                    snackKey={listKey.child('key').val()}
                    snack={listKey.val()}
                    sMealLine={sMealLine}
                    discount={discount}
                    sideDish={sideDish}
                    dessert={dessert}
                    canOrderMealMenu={canOrderMealMenu}
                    hideBottomBorder={hasEverydayMeals && index === setMenuItems.length - 1}
                />
            )
        }

        {hasEverydayMeals && <>
            <div className={'divider-line'}></div>
            <div style={{ marginTop: '20px' }}>
                <div className='popup-heading-meals' style={{ marginTop: '0px' }}>{orderStrings.mealsPopupEverydayChoicesHeading}</div>
            </div>
            <div className={'menu-description'}>{orderStrings.mealsPopupEverydayChoicesDescription}</div>

            {typesList.filter(s_type => s_type.child('order').val() > 0).map(sType => {
                const isMainCourse = sType.key === 'main';

                let filteredMeals: DataSnapshot<Meal>[];

                if (isMainCourse) {
                    filteredMeals = mealsList.filter(sMeal =>
                        sMeal.child('courseType').val() === sType.key &&
                        sMeal.child('order')?.val() >= 0 &&
                        !sMeal.child('isSide')?.val() &&
                        !sMeal.child('isDessert')?.val()
                    );
                } else {
                    const courseTypeFiltered = dailyMealsList?.filter(sMeal =>
                        sMeal.child('courseType').val() === sType.key &&
                        sMeal.child('order')?.val() >= 0 &&
                        !sMeal.child('isSide')?.val() &&
                        !sMeal.child('isDessert')?.val() &&
                        sMeal.child('isExchangable')?.val() === true
                    ) || [];
                    filteredMeals = filterMealsByDateAvailability(courseTypeFiltered, date);
                }

                return <React.Fragment key={sType.key} >
                    {sType.key != 'raw' && <>
                        {filteredMeals?.length > 0 && <MealPopupCategory heading={sType.child(lang).val()} subHeading={sType.child("description").child(lang).val()} />}
                        {filteredMeals.map(s_line => <MealPopUpLine key={s_line.key}
                            date={date}
                            snackKey={s_line.key}
                            snack={s_line.val()}
                            sMealLine={sMealLine}
                            discount={discount}
                            sideDish={sideDish}
                            dessert={dessert}
                            canOrderMealMenu={canOrderMealMenu}
                            hideBottomBorder={s_line.key === lastEverydayMealKey}
                        />
                        )}
                    </>}
                </React.Fragment>
            })}
        </>}
    </div>
}

type PackageMealsPopUpProps = {
    date: string
    mealsList: DataSnapshot<Meal>[];
    dailyMealsList: DataSnapshot<Meal>[];
    typesList: DataSnapshot[];
    sMealLine: DataSnapshot;
    discount: number;
}

function MealPopUpLine({ date, snackKey, snack, sMealLine, discount, sideDish, dessert, canOrderMealMenu, hideBottomBorder }: SnackPopUpLineProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const [schoolData] = useDatabaseVal<SchoolData>(SCHOOL_DATA_PATH);

    const imageUrl = useStorageUrl(snack?.imgUrl);

    const [quantitySide, setQuantitySide] = useSnapshotState<number>(sMealLine, 'quantitySide');
    const [quantityDessert, setQuantityDessert] = useSnapshotState<number>(sMealLine, 'quantityDessert');

    const { uid } = auth.currentUser;
    const mealLine: MealLine = sMealLine?.val();
    const recipient = mealLine?.recipient;
    const schoolBreak = mealLine?.schoolBreak;

    const students = useDatabase(studentsPath(uid));
    const recipientData: DataSnapshot<OrderPerson> = useDatabase(students ? `${studentsPath(uid)}/${recipient}` : null);
    const recipientPerson = recipientData?.val();
    const recipientIsStaff = !!recipientPerson?.personnel;
    const recipientIsStudent = !!recipientPerson?.student;

    const staffDiscount: number = useDatabase(DISCOUNT_STAFF_PATH)?.val() || 0;

    const isSetMenu = snack.courseType === 'main';
    const isMenu1 = snack.menuType === 1;
    const isSelected = snackKey === mealLine.key;

    const isSetMenuDisabled = isSetMenu && !canOrderMealMenu;
    const titleDimmed = isSetMenuDisabled;

    const menu1: Meal = useDatabase(`${menuPath(currentMenu)}/${date}/menu1`)?.val();
    const menu1Price = recipientIsStudent ? menu1?.priceSmall : recipientIsStaff ? getPersonnelPrice(menu1?.price, staffDiscount) : menu1?.price;

    const dayNum = toDateFromServer(date).getDay();

    const mealSide = snack?.sideDish?.[lang];
    const sideDay: string = useDatabase(`${SIDE_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const sideMenu: string = !sideDish ? '' : getLangValue(sideDish.name, lang);
    const sideName: string = !mealSide ? !sideMenu ? sideDay : sideMenu : mealSide;

    const dessertDay: string = useDatabase(`${DESSERT_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const dessertMenu: string = !dessert ? '' : getLangValue(dessert.name, lang);
    const dessertName: string = !dessertMenu ? dessertDay : dessertMenu;

    const hasSmallPortion = !!snack?.priceSmall && snack?.priceSmall > 0;
    const hasMediumPortion = !!snack?.priceMedium && snack?.priceMedium > 0;
    const hasNormalPortion = !!snack?.price && snack?.price > 0;
    const hasMorePortionSizes = (hasSmallPortion && hasMediumPortion) || (hasMediumPortion && hasNormalPortion) || (hasNormalPortion && hasSmallPortion);
    const allowChangePortions = modelConstants?.allowChangePortions && hasMorePortionSizes;
    const { small: smallPortionName, medium: mediumPortionName, large: normalPortionName } = usePortionSizeLabels();

    const portionSize = useMemo(() => {
        if (recipientIsStaff)
            return PORTION_SIZE_NORMAL;

        if (schoolData?.schoolType === SCHOOL_TYPE_NURSERY)
            return PORTION_SIZE_SMALL;
        if (schoolData?.schoolType === SCHOOL_TYPE_PRIMARY)
            return PORTION_SIZE_MEDIUM;
        if (schoolData?.schoolType === SCHOOL_TYPE_SECONDARY)
            return PORTION_SIZE_NORMAL;

        return PORTION_SIZE_NORMAL;
    }, [recipientIsStaff, schoolData]);

    const portionSelectValue = portionSize.toString();
    const shouldShowPortionSizeValue = shouldShowPortionSize(snack, undefined, hasSmallPortion, hasMediumPortion, hasNormalPortion);

    const exchangePrice = getPackageExchangeMealPrice(
        recipientIsStudent,
        recipientIsStaff,
        menu1Price,
        snack,
        discount,
        staffDiscount,
        1,
        modelConstants?.chargePackageExchangePrice,
    );
    const showExchangePrice = modelConstants?.chargePackageExchangePrice && !isMenu1 && (exchangePrice ?? 0) > 0;
    const exchangePriceStr = showExchangePrice ? formatPriceEuro(Number(exchangePrice ?? 0), lang) : null;

    const menuTitle = isSetMenu ? `${orderStrings.mealsPopupMenuWordLabel} ${snack.menuType}` : undefined;

    const allergensCatalog: Allergens = useDatabase(ALLERGENS_PATH)?.val();
    const mealAllergenLabels = useMemo(
        () => getMealAllergenLabels(snack.allergens, allergensCatalog, lang),
        [snack.allergens, allergensCatalog, lang],
    );
    const hasMealAllergens = mealAllergenLabels.length > 0;
    const hasDietaryAttrs = !!snack.bio || !!snack.vegan || !!snack.glutenFree || !!snack.frozen;

    const toggleSide = (isChecked: boolean) => {
        if (isChecked)
            sMealLine?.ref.child('quantitySide').set(1);
        else
            sMealLine.ref.child('quantitySide').remove();
    }
    const toggleDessert = (isChecked: boolean) => {
        if (isChecked)
            sMealLine?.ref.child('quantityDessert').set(1);
        else
            sMealLine.ref.child('quantityDessert').remove();
    }

    const onChangeMeal = () => {
        if (!recipient) {
            return;
        }
        void sMealLine.ref.update({
            quantity: 1,
            key: snackKey,
            recipient,
            schoolBreak,
            isPackage: true,
            packagePrice: exchangePrice ?? 0,
            orderItemRemark: null,
        });
        if (quantitySide) {
            sMealLine.ref.child('quantitySide').set(quantitySide);
        } else {
            sMealLine.ref.child('quantitySide').remove();
        }
        if (quantityDessert) {
            sMealLine.ref.child('quantityDessert').set(quantityDessert);
        } else {
            sMealLine.ref.child('quantityDessert').remove();
        }
    }

    return <MealPopupFoodItemLayout
        hideBottomBorder={hideBottomBorder}
        selected={isSelected}
        setMenuTitle={menuTitle}
        setMenuTitleDimmed={isSetMenuDisabled}
        titleDimmed={titleDimmed}
        title={getLangValue(snack.name, lang)}
        description={getLangValue(snack.description, lang)}
        dietary={hasDietaryAttrs &&
            <div className="meal-popup-food-item__dietary">
                {!!snack.bio && <img className="meal-attr-icons" src="/img-assets/bio.svg" title={commonStrings.bio} alt="" />}
                {!!snack.vegan && <img className="meal-attr-icons" src="/img-assets/vegan.svg" title={commonStrings.vegetarian} alt="" />}
                {!!snack.glutenFree && <img className="meal-attr-icons" src="/img-assets/gluten-free.svg" title={commonStrings.glutenFree} alt="" />}
                {!!snack.frozen && <img className="meal-attr-icons" src="/img-assets/frozen.svg" title={commonStrings.frozen} alt="" />}
            </div>
        }
        allergens={hasMealAllergens &&
            <div className="meal-popup-food-item__allergens">
                <MealAllergensIndicator labels={mealAllergenLabels} buttonLabel={orderStrings.mealsPopupAllergensLabel} />
            </div>
        }
        checkboxes={isSetMenu &&
            <div className="meal-popup-food-item__checkboxes">
                {!!sideName &&
                    <div className={`popup-title-line-meals ${quantitySide > 0 ? 'selected' : ''}`}>
                        <div className={`popup-title ${isSetMenuDisabled && 'opac5' || ''}`} style={{ flex: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        disabled={!modelConstants?.allowPackageSide || isSetMenuDisabled}
                                        checked={quantitySide > 0}
                                        onChange={e => toggleSide(e.target.checked)}
                                        sx={{ padding: '4px', color: 'var(--lb_green)', '&.Mui-checked': { color: 'var(--lb_green)' } }}
                                    />
                                }
                                label={sideName}
                                sx={{
                                    '& .MuiFormControlLabel-label': { fontSize: '14px', color: 'var(--secondary_text)' },
                                    marginLeft: '0px',
                                }}
                            />
                        </div>
                    </div>
                }

                {!!dessertName &&
                    <div className={`popup-title-line-meals ${quantityDessert > 0 ? 'selected' : ''}`}>
                        <div className={`popup-title ${isSetMenuDisabled && 'opac5' || ''}`} style={{ flex: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        disabled={!modelConstants?.allowPackageDessert || isSetMenuDisabled}
                                        checked={quantityDessert > 0}
                                        onChange={e => toggleDessert(e.target.checked)}
                                        sx={{ padding: '4px', color: 'var(--lb_green)', '&.Mui-checked': { color: 'var(--lb_green)' } }}
                                    />
                                }
                                label={dessertName}
                                sx={{
                                    '& .MuiFormControlLabel-label': { fontSize: '14px', color: 'var(--secondary_text)' },
                                    marginLeft: '0px',
                                }}
                            />
                        </div>
                    </div>
                }
            </div>
        }
        imageUrl={imageUrl}
        imageAlt={getLangValue(snack.name, lang)}
        footerPrice={exchangePriceStr && <span className="meal-popup-food-item__price-final">{exchangePriceStr}</span>}
        footerActions={<>
            {shouldShowPortionSizeValue &&
                <FormControl size="small" className="meal-popup-portion-wrap">
                    <Select
                        className="meal-popup-portion-select"
                        value={portionSelectValue}
                        disabled={true}
                        MenuProps={{
                            disableScrollLock: true,
                            slotProps: {
                                root: {
                                    sx: { zIndex: MEALS_POPUP_OVERLAY_Z_INDEX },
                                },
                            },
                        }}
                        sx={{
                            height: 32,
                            minWidth: 80,
                            borderRadius: '8px',
                            backgroundColor: 'var(--order_card_bg)',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': {
                                py: '8px',
                                pl: '8px',
                                pr: '24px',
                                fontSize: '12px',
                                lineHeight: '14px',
                                letterSpacing: '0.21px',
                                color: 'var(--black_txt_title)',
                            },
                        }}
                    >
                        {hasSmallPortion && <MenuItem value={PORTION_SIZE_SMALL.toString()}>{smallPortionName}</MenuItem>}
                        {hasMediumPortion && <MenuItem value={PORTION_SIZE_MEDIUM.toString()}>{mediumPortionName}</MenuItem>}
                        {hasNormalPortion && <MenuItem value={PORTION_SIZE_NORMAL.toString()}>{normalPortionName}</MenuItem>}
                    </Select>
                </FormControl>
            }
            <button
                type="button"
                className={`change-package-button ${isSelected ? 'change-package-selected' : ''}`}
                disabled={isSelected || isSetMenuDisabled}
                onClick={onChangeMeal}
            >
                {isSelected ? orderStrings.packageMealsPopupSelected : orderStrings.packageMealsPopupSelect}
            </button>
        </>}
    />;
}

type SnackPopUpLineProps = {
    date: string
    snackKey: string;
    snack: Meal
    sMealLine: DataSnapshot;
    discount: number;
    sideDish: Meal;
    dessert: Meal;
    canOrderMealMenu: boolean;
    hideBottomBorder?: boolean;
}
